import React, { Fragment, useState, useEffect, useContext, ReactElement } from "react";
import { gql, useQuery } from "urql";
import { useParams, useLocation, Link, useSearchParams } from "wouter";
import { Formik, Form } from "formik";

import { cloneDeep, keyBy } from "lodash";

import { SelectField } from "../../../components/src/form";
import { emptyObjectId } from "../../../common/src";

import { ClientContext, IClient } from "../../../components/src/contexts/ClientContext";
import { PartyStatusEnum, PartyType, PermissionAssetEnum } from "../../../common/src/types.generated";

export const GET_ClIENTS = gql`
    query getClients($typeIn: [PartyType!], $statusIn: [PartyStatusEnum!]) {
        clients: parties(filter: { typeIn: $typeIn, withTransactions: true, statusIn: $statusIn }) {
            _id
            name
            dashName
        }
    }
`;

const getClientsByAssets = gql`
    query {
        clientsByAssets {
            asset
            clients {
                _id
            }
        }
    }
`;

interface IClientContextSelectorProps {
    typeIn: PartyType[];
    defaultClientName?: string;
    carryQueryArgs?: boolean;
    statusIn?: PartyStatusEnum[];
    asset?: PermissionAssetEnum;
}

/** Join path segments and drop empties so `/accountperformance/` + client does not become `//`. */
export function joinClientPath(pathname: string, extraSegment?: string): string {
    const parts = pathname.split("/").filter((part) => part.length > 0);
    if (extraSegment && extraSegment.length > 0 && parts[parts.length - 1] !== extraSegment) {
        parts.push(extraSegment);
    }
    return "/" + parts.join("/");
}

export function ClientContextSelector({
    typeIn = [PartyType.Client],
    defaultClientName = "Select client",
    carryQueryArgs = true,
    statusIn = [PartyStatusEnum.Confirmed],
    asset = null
}: IClientContextSelectorProps): React.ReactElement {
    const { client, setClient } = useContext(ClientContext);

    const [location, navigate] = useLocation();
    const allParams = useParams<{ clientDashName: string }>();
    const [formData, setFormData] = useState({ clientName: defaultClientName });
    const clientDashName = allParams.clientDashName || undefined;
    const [queryArgs] = useSearchParams();
    const [{ fetching, error, data }] = useQuery({ query: GET_ClIENTS, variables: { typeIn: typeIn, statusIn } });
    const [{ data: dataClientsByAssets, fetching: loadingClientsByAssets, error: errorClientsByAssets }] = useQuery({
        query: getClientsByAssets
    });

    const defaultClient = React.useMemo(() => {
        return { _id: emptyObjectId, name: defaultClientName, dashName: defaultClientName.replace(/ /g, "-") };
    }, [defaultClientName]);

    const dataClients: {
        _id: string;
        name: string;
        dashName: string;
    }[] = React.useMemo(() => {
        if (data && data.clients) {
            let clients = cloneDeep(data.clients);
            let clientIds: string[] = [];
            if (asset && dataClientsByAssets && dataClientsByAssets.clientsByAssets) {
                const clientsByAssets = keyBy(dataClientsByAssets.clientsByAssets, "asset");
                if (clientsByAssets[asset]) {
                    clientIds = clientsByAssets[asset].clients.map((client) => client._id);
                }
            }

            if (asset) {
                clients = data.clients.filter((clientData) => clientIds.includes(clientData._id));
            }

            const result: {
                _id: string;
                name: string;
                dashName: string;
            }[] = [];
            for (const client of clients) {
                result.push(client);
            }
            return [defaultClient, ...result];
        } else {
            return [defaultClient];
        }
    }, [asset, data, dataClientsByAssets, defaultClient]);

    const { dashNameLookup, nameLookup } = React.useMemo(() => {
        const dashNameLookup: Record<string, IClient> = {};
        const nameLookup: Record<string, IClient> = {};
        const idLookup: Record<string, IClient> = {};
        for (const client of dataClients) {
            dashNameLookup[client.dashName] = client;
            nameLookup[client.name] = client;
            idLookup[client._id] = client;
        }
        return { dashNameLookup, nameLookup, idLookup };
    }, [dataClients]);

    useEffect(() => {
        if (!data && client.name !== defaultClientName) {
            setFormData({ clientName: defaultClientName });
            setClient(defaultClient);
        } else if (data && !fetching) {
            // Only proceed if data is loaded and not currently fetching (prevents race conditions)
            // Guard against undefined lookup to prevent 404 errors
            if (client._id === emptyObjectId && !clientDashName && formData.clientName !== defaultClientName) {
                setFormData({ clientName: defaultClientName });
                return;
            }

            const formDataClient = nameLookup[formData.clientName];
            if (!formDataClient) {
                // If formData client doesn't exist in lookup, reset to default
                setFormData({ clientName: defaultClientName });
                return;
            }
            
            // init formData from client provided in url params
            const selectedClientDashName = formDataClient.dashName;
            if (clientDashName && clientDashName !== selectedClientDashName) {
                const c = dashNameLookup[clientDashName];
                if (c) {
                    setFormData({
                        clientName: c.name
                    });
                    setClient(c);
                }
            } else if (!clientDashName && client.name !== formData.clientName && client._id !== emptyObjectId) {
                // init formData from a real client already in ClientContext — never push the placeholder
                const c = nameLookup[client.name];
                if (c) {
                    const pathname = location.split("?")[0].split("#")[0];
                    const nextPath = joinClientPath(pathname, client.dashName);
                    if (carryQueryArgs) {
                        navigate(nextPath + (queryArgs.toString() ? `?${queryArgs.toString()}` : ""));
                    } else {
                        navigate(nextPath);
                    }
                }
            }
        }
    }, [
        carryQueryArgs,
        client._id,
        client.dashName,
        client.name,
        clientDashName,
        dashNameLookup,
        data,
        dataClients,
        defaultClient,
        defaultClientName,
        fetching,
        formData.clientName,
        location,
        nameLookup,
        navigate,
        queryArgs,
        setClient
    ]);

    if (fetching || loadingClientsByAssets) return <p>Loading...</p>;
    if (error) return <p>Error!</p>;
    if (errorClientsByAssets)
        return (
            <p>
                Error clients: {errorClientsByAssets.message ? errorClientsByAssets.message : JSON.stringify(errorClientsByAssets, null, 2)}
            </p>
        );

    const clientNames =
        dataClients.length > 1
            ? [
                  dataClients[0].name,
                  ...dataClients
                      .slice(1, dataClients.length)
                      .map((client) => client.name)
                      .sort()
              ]
            : [dataClients[0].name];

    return (
        <Formik
            enableReinitialize={true}
            initialValues={formData}
            validate={(validateFormData) => {
                const errors: any = {};
                if (data) {
                    const orgPath = location.split("?")[0].split("#")[0];
                    if (nameLookup[validateFormData.clientName]) {
                        if (validateFormData.clientName !== formData.clientName) {
                            const nextDashName =
                                validateFormData.clientName === defaultClientName
                                    ? undefined
                                    : nameLookup[validateFormData.clientName].dashName;
                            let nextPath: string;
                            if (clientDashName && nextDashName) {
                                nextPath = joinClientPath(decodeURI(orgPath).replace(allParams["clientDashName"], nextDashName));
                            } else if (nextDashName) {
                                nextPath = joinClientPath(orgPath, nextDashName);
                            } else {
                                nextPath = joinClientPath(orgPath.replace("/" + (allParams["clientDashName"] || ""), ""));
                            }
                            if (carryQueryArgs) {
                                navigate(nextPath + (queryArgs.toString() ? `?${queryArgs.toString()}` : ""));
                            } else {
                                navigate(nextPath);
                            }
                            if (validateFormData.clientName === defaultClientName) {
                                setClient(defaultClient);
                            }
                        }
                    } else if (validateFormData.clientName === defaultClientName) {
                        const nextPath = joinClientPath(orgPath.replace("/" + (allParams["clientDashName"] || ""), ""));
                        if (carryQueryArgs) {
                            navigate(nextPath + (queryArgs.toString() ? `?${queryArgs.toString()}` : ""));
                        } else {
                            navigate(nextPath);
                        }
                        setClient(defaultClient);
                    }
                }
                return Object.keys(errors).length > 0 ? errors : {};
            }}
            onSubmit={null}
        >
            {({ isSubmitting }) => {
                let label: string | ReactElement = "Client";
                if (client && client._id && client._id !== "000000000000000000000000") {
                    label = <Link to={"/parties/" + client._id}>Client</Link>;
                }
                return (
                    <Fragment>
                        <Form autoComplete="off">
                            <SelectField
                                className="select-client-control"
                                name="clientName"
                                label={label}
                                options={clientNames}
                                disabled={isSubmitting}
                            />
                        </Form>
                    </Fragment>
                );
            }}
        </Formik>
    );
}
